import { type AxiosError } from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { PaginationRequest } from "@/shared/types";
import { type PaginatedResponse } from "@/shared/types";
import { apiInstance } from "@/shared/api";
import type {
  AssignTeamleadToMasterTeamleadRequest,
  MasterTeamLeadCredentialsResponse,
  TransferTeamleadToMasterTeamleadRequest,
} from "./api.dto";
import { filterObjectKeys } from "@/shared/lib/utils";
import { Teamlead } from "@/entities/teamlead";

export const useMasterTeamleadsTeamleads = (
  params: PaginationRequest & { masterId: string; enabled: boolean },
) => {
  return useQuery<PaginatedResponse<Teamlead>, AxiosError>({
    queryKey: [
      "masterteamleads-teamleads",
      filterObjectKeys(params, ["enabled", "masterId"]),
    ],
    queryFn: async () => {
      try {
        const response = await apiInstance.get<PaginatedResponse<Teamlead>>(
          `/teamleads/master-teamlead/${params.masterId}`,
          filterObjectKeys(params, ["enabled", "masterId"]),
          true,
        );

        return response.data;
      } catch (e) {
        console.error(e);
        throw new Error(e as string | undefined);
      }
    },
    enabled: params.enabled,
  });
};

export const useUpdateTeamlead = ({ masterId }: { masterId: string }) => {
  return useMutation({
    mutationFn: async (data: { token: string; name: string }) => {
      try {
        const response = await apiInstance.patch(
          `/teamleads/master/${masterId}`,
          data,
        );

        return response.data;
      } catch (e) {
        console.error(e);
        throw new Error(e as string | undefined);
      }
    },
  });
};

export const useTransferTeamleadToMasterTeamlead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: TransferTeamleadToMasterTeamleadRequest) => {
      try {
        const response = await apiInstance.post(
          `/teamleads/${data.teamleadId}/transfer-to-master-teamlead`,
          {
            masterTeamleadId: data.masterTeamleadId,
          },
          undefined,
          true,
        );

        return response.data;
      } catch (e) {
        console.error(e);
        throw new Error(e as string | undefined);
      }
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ["teamleads"] });
    },
  });
};

export const useAssignTeamleadToMasterTeamlead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AssignTeamleadToMasterTeamleadRequest) => {
      try {
        const response = await apiInstance.post(
          `/teamleads/${data.teamleadId}/assign-master-teamlead`,
          {
            masterTeamleadId: data.masterTeamleadId,
          },
          undefined,
          true,
        );

        return response.data;
      } catch (e) {
        console.error(e);
        throw new Error(e as string | undefined);
      }
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ["teamleads"] });
    },
  });
};

export const useTeamleadCredentials = ({
  accountId,
  enabled,
}: {
  accountId: string;
  enabled: boolean;
}) => {
  return useQuery<MasterTeamLeadCredentialsResponse>({
    queryKey: ["teamlead", accountId],
    queryFn: async () => {
      try {
        const response =
          await apiInstance.get<MasterTeamLeadCredentialsResponse>(
            `/teamleads/${accountId}/credentials`,
          );

        return response.data;
      } catch (e) {
        console.error(e);
        throw new Error(e as string | undefined);
      }
    },
    enabled,
  });
};
